import type { BusinessServiceRecord } from '../api/schemas';

/**
 * The transient in-memory Cobrar cart (docs/CONCEPTS.md — "Línea"). It never
 * persists: a Cobro is born complete or not at all (docs/FLOWS.md §1). A
 * `serviceId` of `null` marks a free-form "Otro artículo" line, whose name
 * and price were typed at the counter rather than looked up.
 */
export type CartLine = {
  id: string;
  serviceId: string | null;
  name: string;
  unitPriceCents: number;
  quantity: number;
  /** Set on the one Línea that charges what a partly paid Reserva still owes. */
  settlesBookingId?: string;
};

/**
 * How the saldo Línea reads in the cart, on the ticket and in Reportes. The API
 * reads this exact prefix (BOOKING_BALANCE_LINE_PREFIX) to guard the saldo.
 */
export const BALANCE_LINE_PREFIX = 'Saldo: ';

/**
 * The one Línea Pagos writes. Linked to a Reserva, the API counts it toward the
 * service by this exact name (BOOKING_PAYMENT_LINE_NAME).
 */
export const BOOKING_PAYMENT_LINE_NAME = 'Pago';

/** A visit being linked to the cart, and what its ledger already received. */
export type LinkedBookingCharge = {
  bookingId: string;
  /** Its service in the active catalog; absent when it was retired. */
  service?: BusinessServiceRecord;
  /** The service's name, which still labels the saldo when it was retired. */
  serviceName: string;
  /** From the booking balance; absent when it could not be read. */
  balance?: { paidCents: number; balanceCents: number; serviceCharged?: boolean; packCovered?: boolean };
};

/** Tapping a service adds a new Línea, or increments qty if it's already in the cart. */
export function addServiceLine(
  lines: readonly CartLine[],
  service: BusinessServiceRecord,
): CartLine[] {
  const existing = lines.find((line) => line.serviceId === service.id);
  if (existing) {
    return lines.map((line) =>
      line.id === existing.id ? { ...line, quantity: line.quantity + 1 } : line,
    );
  }
  return [
    ...lines,
    {
      id: crypto.randomUUID(),
      serviceId: service.id,
      name: service.name,
      unitPriceCents: service.priceCents,
      quantity: 1,
    },
  ];
}

/** "Otro artículo": always a new Línea, never merged with an existing one by name. */
export function addFreeformLine(
  lines: readonly CartLine[],
  name: string,
  unitPriceCents: number,
): CartLine[] {
  return [...lines, { id: crypto.randomUUID(), serviceId: null, name, unitPriceCents, quantity: 1 }];
}

/**
 * The stepper: a quantity of cero or less removes the Línea (FLOWS.md §1:
 * "quita" at zero). A saldo is owed once, so it never goes above one.
 */
export function setLineQuantity(
  lines: readonly CartLine[],
  lineId: string,
  quantity: number,
): CartLine[] {
  if (quantity <= 0) return lines.filter((line) => line.id !== lineId);
  return lines.map((line) =>
    line.id === lineId && !(line.settlesBookingId && quantity > 1) ? { ...line, quantity } : line,
  );
}

/**
 * The cart once a visit is linked (or unlinked, with `null`). Any saldo of the
 * visit it replaces leaves first. Then, going by what the ledger says: service
 * not charged yet (an extra paid first does not count), its service joins an
 * EMPTY cart at the catalog price, as always; charged with some still owed, one
 * typed Línea "Saldo: <servicio>" at exactly that, whatever else is in the cart,
 * and never the service at full price again; paid in full or by a package,
 * nothing. Unread payments keep the old rule, and the server refuses a service
 * charged twice.
 */
export function linesForLinkedBooking(
  lines: readonly CartLine[],
  visit: LinkedBookingCharge | null,
): CartLine[] {
  const kept = lines.filter((line) => !line.settlesBookingId);
  if (!visit) return kept;
  const { balance } = visit;
  if (balance?.packCovered) return kept;
  if (balance && serviceWasCharged(balance)) {
    return balance.balanceCents > 0 ? [...kept, balanceLine(visit, balance.balanceCents)] : kept;
  }
  return visit.service && kept.length === 0 ? addServiceLine(kept, visit.service) : kept;
}

/**
 * Usar paquete paid the visit's service: it leaves the cart, every unit of it,
 * because the server charges none of it on that visit anymore (the same as a
 * visit paid in full). Whatever else was added stays to charge.
 */
export function linesAfterPackUse(lines: readonly CartLine[], serviceId: string): CartLine[] {
  return lines.filter((line) => line.serviceId !== serviceId);
}

/** The API says so; an older one that does not is read by what it received. */
function serviceWasCharged(balance: NonNullable<LinkedBookingCharge['balance']>): boolean {
  return balance.serviceCharged ?? balance.paidCents > 0;
}

function balanceLine(visit: LinkedBookingCharge, balanceCents: number): CartLine {
  return {
    id: crypto.randomUUID(),
    serviceId: null,
    name: `${BALANCE_LINE_PREFIX}${visit.serviceName}`,
    unitPriceCents: balanceCents,
    quantity: 1,
    settlesBookingId: visit.bookingId,
  };
}

export type CatalogReconciliation = {
  lines: CartLine[];
  droppedNames: string[];
};

/**
 * 409 total_mismatch recovery (docs/FLOWS.md §1 edges): re-price every
 * service-backed Línea against the freshly fetched active catalog, dropping
 * any whose Servicio disappeared (deactivated mid-cart, or removed) and
 * naming it for the notice. Free-form lines pass through untouched: their
 * price was typed, never sourced from the catalog.
 */
export function reconcileWithCatalog(
  lines: readonly CartLine[],
  activeServices: readonly BusinessServiceRecord[],
): CatalogReconciliation {
  const droppedNames: string[] = [];
  const reconciled: CartLine[] = [];

  for (const line of lines) {
    if (line.serviceId === null) {
      reconciled.push(line);
      continue;
    }
    const live = activeServices.find((service) => service.id === line.serviceId);
    if (!live) {
      droppedNames.push(line.name);
      continue;
    }
    reconciled.push({ ...line, name: live.name, unitPriceCents: live.priceCents });
  }

  return { lines: reconciled, droppedNames };
}

/**
 * Only offers the counter may legally apply right now. The server recomputes
 * the total from the live catalog and 409s on an expired offer, so the picker
 * must never list one outside its validity window.
 */
export function currentOffers<T extends { isActive: boolean; startsAt: string; validUntil: string }>(
  offers: T[],
  now: Date,
): T[] {
  return offers.filter(
    (offer) =>
      offer.isActive && new Date(offer.startsAt) <= now && now <= new Date(offer.validUntil),
  );
}
